import { useMemo } from "react";
import { usePureStudentUrls } from "@crehana/auth";
import { elevationDark, neutralLight } from "@crehana/tailwindcss";
import cn from "clsx";
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import styled from "styled-components";
import useNewDashboardTranslations from "../../hooks/useNewDashboardTranslations";
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_EXPANDED_WIDTH } from "../../constants";
import useNewDashboardContext from "../../context/useNewDashboardContext";
import ActionCollapse from "./components/ActionCollapse";
import BottomIconSidebar from "./components/BottomIconSidebar";
import ContentItemsSidebar from "./components/ContentItemsSidebar";
import NewSidebarV2Header from "./components/NewSidebarV2Header";
import ProductSidebar from "./components/ProductSidebar";
import TopIconSidebar from "./components/TopIconSidebar";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var SidebarContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-qcq3t4-0"
})(["\n  width: ", ";\n  ", ";\n\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n\n  transition: ", ";\n\n  ", "\n"], function (_ref) {
  var width = _ref.width;
  return width;
}, function (_ref2) {
  var minWidth = _ref2.minWidth;
  return typeof minWidth !== 'undefined' && "min-width: ".concat(minWidth, ";");
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.transition(['width', 'min-width']);
}, function (_ref4) {
  var isDark = _ref4.isDark,
    _ref4$hideBoxShadow = _ref4.hideBoxShadow,
    hideBoxShadow = _ref4$hideBoxShadow === void 0 ? false : _ref4$hideBoxShadow,
    theme = _ref4.theme;
  return hideBoxShadow || isDark ? '' : "box-shadow: 0px 1px 16px ".concat(theme.colors.gray.main, ";");
});
var Sidebar = function Sidebar(_ref5) {
  var renderLinks = _ref5.renderLinks,
    renderBottomLinks = _ref5.renderBottomLinks,
    renderUpperLinks = _ref5.renderUpperLinks,
    customHomeUrl = _ref5.customHomeUrl,
    tourSelector = _ref5.tourSelector,
    fullWidth = _ref5.fullWidth,
    _ref5$hideBoxShadow = _ref5.hideBoxShadow,
    hideBoxShadow = _ref5$hideBoxShadow === void 0 ? false : _ref5$hideBoxShadow,
    _ref5$hideActionColla = _ref5.hideActionCollapseButton,
    hideActionCollapseButton = _ref5$hideActionColla === void 0 ? false : _ref5$hideActionColla,
    organizationLogoProp = _ref5.organizationLogo,
    organizationSlug = _ref5.organizationSlug,
    className = _ref5.className,
    iconCollapse = _ref5.iconCollapse,
    canRenderProductDropdown = _ref5.canRenderProductDropdown,
    isSidebarV2Enabled = _ref5.isSidebarV2Enabled,
    hasTalentProduct = _ref5.hasTalentProduct,
    isProduction = _ref5.isProduction;
  var _useNewDashboardTrans = useNewDashboardTranslations(),
    t = _useNewDashboardTrans.t;
  var _useNewDashboardConte = useNewDashboardContext(),
    isDark = _useNewDashboardConte.isDark,
    user = _useNewDashboardConte.user,
    sidebarCollapsed = _useNewDashboardConte.sidebarCollapsed,
    designMode = _useNewDashboardConte.designMode,
    toggleSideBar = _useNewDashboardConte.toggleSideBar;
  // @ts-expect-error Type 'MeObject | undefined' is not assignable to type 'Pick<MeObject, "isActiveDegree" | "isB2B" | "isPremium" | "organization"> | undefined'.
  var _usePureStudentUrls = usePureStudentUrls({
      me: user
    }),
    homeUrl = _usePureStudentUrls.homeUrl;
  var menuWidth = useMemo(function () {
    if (fullWidth) {
      return '100%';
    }
    return sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;
  }, [fullWidth, sidebarCollapsed]);
  var alternativeBackgroundColor = isDark ? elevationDark['01'] : neutralLight[100];
  var regularBackgroundColor = isDark ? '#181b32' : neutralLight[100];
  var backgroundColor = isSidebarV2Enabled ? alternativeBackgroundColor : regularBackgroundColor;
  return /*#__PURE__*/_jsxs(SidebarContainer, {
    width: menuWidth,
    isDark: isDark,
    hideBoxShadow: hideBoxShadow,
    className: cn(className, "font-nun flex-shrink-0", isSidebarV2Enabled ? 'h-full ' : 'h-screen'),
    style: {
      backgroundColor: backgroundColor,
      zIndex: !designMode && !isSidebarV2Enabled ? '1000' : 'auto'
    },
    children: [isSidebarV2Enabled ? /*#__PURE__*/_jsx(NewSidebarV2Header, {
      sidebarCollapsed: !!sidebarCollapsed,
      hasTalentProduct: hasTalentProduct,
      isDark: isDark,
      organizationSlug: organizationSlug
    }) : /*#__PURE__*/_jsx(TopIconSidebar, {
      isDark: isDark,
      logoUrl: customHomeUrl || homeUrl,
      tourSelector: tourSelector,
      sidebarCollapsed: !!sidebarCollapsed,
      organizationLogo: organizationLogoProp,
      hideActionCollapseButton: hideActionCollapseButton,
      toggleSideBar: toggleSideBar,
      user: user,
      isSidebarV2Enabled: isSidebarV2Enabled
    }), !hideActionCollapseButton && /*#__PURE__*/_jsx(ActionCollapse, {
      isOpen: !sidebarCollapsed,
      onClick: toggleSideBar,
      isDark: isDark,
      iconCollapse: iconCollapse,
      title: t(sidebarCollapsed ? 'BUTTON_COLLAPSE_SIDEBAR_CLOSED' : 'BUTTON_COLLAPSE_SIDEBAR_OPENED'),
      isSidebarV2Enabled: isSidebarV2Enabled,
      hasTalentProduct: hasTalentProduct
    }), Boolean(renderUpperLinks) && /*#__PURE__*/_jsx("div", {
      className: "w-full overflow-x-hidden overflow-y-hidden tw-flex-shrink-0",
      children: renderUpperLinks && renderUpperLinks({
        isDark: isDark,
        classNameLink: 'w-full flex items-center px-20 py-12 focus:outline-none cursor-pointer'
      })
    }), canRenderProductDropdown && /*#__PURE__*/_jsx(ProductSidebar, {
      isDark: isDark,
      sidebarCollapsed: !!sidebarCollapsed,
      isProduction: isProduction
    }), /*#__PURE__*/_jsx(ContentItemsSidebar, {
      isDark: isDark,
      renderLinks: renderLinks,
      renderBottomLinks: renderBottomLinks,
      sidebarCollapsed: !!sidebarCollapsed,
      isSidebarV2Enabled: isSidebarV2Enabled
    }), /*#__PURE__*/_jsx(BottomIconSidebar, {
      user: user,
      isDark: isDark,
      sidebarCollapsed: !!sidebarCollapsed
    })]
  });
};
export default Sidebar;